/**
 * StormChanceUI.tsx — floating menu, timeline/legend, and map controls.
 *
 * Drop-in replacement for the old LayerSidebar, LegendBar, TimeScrubber,
 * and STORMCHANCE badge. All state is owned by the parent (App.tsx) and
 * threaded in via props.
 */
import { useState, useRef, useEffect, useCallback, createContext, useContext } from "react";
import type { LayerConfig } from "./weatherLayerConfig";
import { WEATHER_LAYERS } from "./weatherLayerConfig";
import type { FeatureToggles } from "./weatherStyle";
import type { SpcProduct } from "./stormOverlays";
import { SPC_LEGEND_ITEMS, SPC_PRODUCTS } from "./stormOverlays";
import { IS_FREE, isLayerLocked, isOverlayLocked, isSectionLocked, requestUpsell } from "./tier";

// Small padlock rendered on locked (premium-only) menu items in the free
// build. Sized to sit inline with the 12.5px sub-item labels; amber matches
// the FORECAST pill / upsell toast accent so "locked" reads as one system.
const LockIcon = ({ size = 11 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#f5a623"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// ═══════════════════════════════════════════════════════════════════════════
// Theme system — auto day/night based on local time
// ═══════════════════════════════════════════════════════════════════════════
function getIsNight() {
  const h = new Date().getHours();
  return h < 6 || h >= 20;
}

const THEMES = {
  dark: {
    panelBg: "rgba(10, 14, 22, 0.88)",
    panelBorder: "rgba(94,181,223,0.08)",
    panelShadow: "0 8px 32px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.3)",
    timelineBg: "rgba(8, 10, 16, 0.92)",
    timelineBorder: "rgba(255,255,255,0.06)",
    trackBg: "rgba(255,255,255,0.06)",
    trackDivider: "rgba(255,255,255,0.25)",
    divider: "rgba(255,255,255,0.05)",
    accent: "#5EB5DF",
    accentDark: "#3A8FBF",
    accentGlow: "rgba(94,181,223,0.4)",
    accentSoft: "rgba(94,181,223,0.06)",
    accentMed: "rgba(94,181,223,0.1)",
    accentBorder: "rgba(94,181,223,0.3)",
    accentBg: "rgba(94,181,223,0.15)",
    nowcast: "#7DD3E8",
    nowcastGlow: "rgba(125,211,232,0.5)",
    textPrimary: "#e8ecf2",
    textSecondary: "#7d8a9a",
    textMuted: "#5e6d7e",
    textDim: "#8a96a8",
    textDimmer: "#3d4a5a",
    chevron: "#9aa7b6",
    iconInactive: "#4a5568",
    hoverBg: "rgba(255,255,255,0.025)",
    subHoverBg: "rgba(255,255,255,0.02)",
    activeBg: "rgba(94,181,223,0.06)",
    playBg: "rgba(255,255,255,0.05)",
    playBorder: "rgba(255,255,255,0.08)",
    logoBg: "linear-gradient(135deg, #5EB5DF 0%, #3A8FBF 100%)",
    logoText: "#3F98DA",
    logoAccent: "#0A5E89",
  },
  light: {
    panelBg: "rgba(255, 255, 255, 0.92)",
    panelBorder: "rgba(94,181,223,0.12)",
    panelShadow: "0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
    timelineBg: "rgba(255, 255, 255, 0.95)",
    timelineBorder: "rgba(0,0,0,0.08)",
    trackBg: "rgba(0,0,0,0.07)",
    trackDivider: "rgba(0,0,0,0.2)",
    divider: "rgba(0,0,0,0.06)",
    accent: "#2E8BBE",
    accentDark: "#1D6F9F",
    accentGlow: "rgba(46,139,190,0.3)",
    accentSoft: "rgba(46,139,190,0.06)",
    accentMed: "rgba(46,139,190,0.1)",
    accentBorder: "rgba(46,139,190,0.3)",
    accentBg: "rgba(46,139,190,0.12)",
    nowcast: "#4BB8D8",
    nowcastGlow: "rgba(75,184,216,0.35)",
    textPrimary: "#1a2533",
    textSecondary: "#506070",
    textMuted: "#708090",
    textDim: "#8a9aaa",
    textDimmer: "#a0aab5",
    chevron: "#607080",
    iconInactive: "#8a9aaa",
    hoverBg: "rgba(0,0,0,0.025)",
    subHoverBg: "rgba(0,0,0,0.02)",
    activeBg: "rgba(46,139,190,0.06)",
    playBg: "rgba(0,0,0,0.04)",
    playBorder: "rgba(0,0,0,0.1)",
    logoBg: "linear-gradient(135deg, #2E8BBE 0%, #1D6F9F 100%)",
    logoText: "#3F98DA",
    logoAccent: "#0A5E89",
  },
};

type Theme = typeof THEMES.dark;
const ThemeContext = createContext<Theme>(THEMES.dark);
const useTheme = () => useContext(ThemeContext);

// ═══════════════════════════════════════════════════════════════════════════
// Icons
// ═══════════════════════════════════════════════════════════════════════════
const RadarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <circle cx="12" cy="12" r="10" opacity="0.3" /><circle cx="12" cy="12" r="6" opacity="0.5" />
    <circle cx="12" cy="12" r="2" /><line x1="12" y1="12" x2="18" y2="6" />
  </svg>
);
const SatelliteIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round">
    <rect x="10" y="9" width="4" height="6" rx="1" transform="rotate(45 12 12)" />
    <g strokeWidth="1.4">
      <line x1="3" y1="3" x2="9" y2="9" />
      <line x1="2" y1="6" x2="6" y2="2" strokeWidth="2.2" />
      <line x1="3.5" y1="7.5" x2="7.5" y2="3.5" strokeWidth="2.2" />
      <line x1="5" y1="9" x2="9" y2="5" strokeWidth="2.2" />
    </g>
    <g strokeWidth="1.4">
      <line x1="15" y1="15" x2="21" y2="21" />
      <line x1="18" y1="22" x2="22" y2="18" strokeWidth="2.2" />
      <line x1="16.5" y1="20.5" x2="20.5" y2="16.5" strokeWidth="2.2" />
      <line x1="15" y1="19" x2="19" y2="15" strokeWidth="2.2" />
    </g>
    <circle cx="6.2" cy="17.8" r="3" fill="none" strokeWidth="1.6" />
    <line x1="8.2" y1="15.8" x2="10" y2="14" strokeWidth="1.6" fill="none" />
  </svg>
);
const SkyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 14.5a4 4 0 014-4h.5a5.5 5.5 0 0110.9 1.5H20a3 3 0 010 6H8a4 4 0 01-4-3.5z" />
  </svg>
);
const AirIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13.5V5a2 2 0 014 0v8.5a4 4 0 11-4 0z" />
    <line x1="12" y1="9" x2="12" y2="15" opacity="0.4" />
    <circle cx="12" cy="17" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);
const WindIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.59 4.59A2 2 0 1111 8H2" /><path d="M12.59 19.41A2 2 0 1014 16H2" />
    <path d="M17.73 7.73A2.5 2.5 0 1119.5 12H2" />
  </svg>
);
const LightningIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);
const WarningIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);
const AlertIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><circle cx="12" cy="16.5" r="0.5" fill="currentColor" />
  </svg>
);
// Section icon: cloud with a lightning bolt (Storm Outlook).
const StormOutlookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.5 15.5a4 4 0 000-8 5.5 5.5 0 00-10.6 1.6A3.6 3.6 0 007.5 15.5" />
    <path d="M13 12l-2.7 4.2h3.2L10.8 21" />
  </svg>
);
// Section icon: cyclone spiral (Tropical Systems). Same glyph as the 14px
// sub-item HurricaneIcon below, scaled to the 18px section-icon size.
const TropicalIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M15.5 12c0-5.5-2.4-9.2-7-10.5 2.9 2 3.8 4.6 3.8 7.6" />
    <path d="M8.5 12c0 5.5 2.4 9.2 7 10.5-2.9-2-3.8-4.6-3.8-7.6" />
  </svg>
);
const HurricaneIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="2.6" />
    <path d="M14.6 12c0-5-2.2-8.4-6.4-9.6C10.8 4.2 11.6 6.6 11.6 9.4" />
    <path d="M9.4 12c0 5 2.2 8.4 6.4 9.6-2.6-1.8-3.4-4.2-3.4-7" />
  </svg>
);
// Nested-ring outline evoking SPC's concentric risk areas. One icon shared by
// all four Day 1 products; the per-product accent color differentiates them.
const OutlookIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <ellipse cx="12" cy="12" rx="9.5" ry="6.8" />
    <ellipse cx="12.8" cy="12" rx="5.6" ry="4" />
    <ellipse cx="13.6" cy="12" rx="2" ry="1.5" />
  </svg>
);
const EyeOffIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" /><path d="M14.12 14.12a3 3 0 11-4.24-4.24" />
  </svg>
);
const ChevronDown = ({ open, color }: { open: boolean; color?: string }) => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke={color ?? "currentColor"}
    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ transition: "transform 0.25s cubic-bezier(0.4,0,0.2,1)", transform: open ? "rotate(180deg)" : "rotate(0deg)", opacity: color ? 1 : 0.45 }}>
    <path d="M3 4.5l3 3 3-3" />
  </svg>
);

// ═══════════════════════════════════════════════════════════════════════════
// Brand logo — the StormChance cube
// ═══════════════════════════════════════════════════════════════════════════
// Loads the brand cube image. Place the PNG file here:
//
//     public/scmini.png
//
// (i.e. the project's `public/` directory, alongside index.html). Vite serves
// everything in public/ at the site root, so the path below resolves to it.
// To use a different filename or location, just change BRAND_LOGO_SRC.
export const BRAND_LOGO_SRC = "/scmini.png";
export const BrandCube = ({ size = 26 }: { size?: number }) => (
  <img
    src={BRAND_LOGO_SRC}
    alt="StormChance"
    width={size}
    height={size}
    style={{ display: "block", width: size, height: size, objectFit: "contain" }}
  />
);

// Brand wordmark — matches the logo lockup: italic, bold, two-tone blue.
// STORM in the lighter brand blue, CHANCE in the deeper brand blue.
export const BRAND_STORM = "#3F98DA";
export const BRAND_CHANCE = "#0A5E89";
export const BrandWordmark = ({ size = 15 }: { size?: number }) => (
  <div style={{ overflow: "hidden", whiteSpace: "nowrap", display: "flex", alignItems: "center", lineHeight: 1.4 }}>
    <span style={{ fontSize: size, fontWeight: 800, fontStyle: "italic", color: BRAND_STORM, letterSpacing: -0.2, lineHeight: 1 }}>STORM</span>
    <span style={{ fontSize: size, fontWeight: 800, fontStyle: "italic", color: BRAND_CHANCE, letterSpacing: -0.2, lineHeight: 1 }}>CHANCE</span>
  </div>
);

// ─── Map-feature toggle icons (settings panel) ──────────────────────────────
// Roads: a winding local street. Highways: a road-sign shield with a divided
// lane. Landuse: a tree (parks / green space). Buildings: a small skyline.
const RoadsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 21c0-4 2-6 2-9s-2-5-2-9" opacity="0.55" />
    <path d="M17 21c0-4-2-6-2-9s2-5 2-9" opacity="0.55" />
    <line x1="12" y1="4" x2="12" y2="7" />
    <line x1="12" y1="11" x2="12" y2="14" />
    <line x1="12" y1="18" x2="12" y2="21" />
  </svg>
);
const HighwaysIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2.5l7 2.2v6.1c0 4.4-3 8.2-7 10.7-4-2.5-7-6.3-7-10.7V4.7L12 2.5z" />
    <line x1="12" y1="7" x2="12" y2="17" strokeDasharray="2 2.4" opacity="0.7" />
  </svg>
);
const LanduseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3c-3 0-5 2.4-5 5.2 0 2.3 1.5 4.2 3.6 4.9V21h2.8v-7.9C15.5 12.4 17 10.5 17 8.2 17 5.4 15 3 12 3z" />
    <line x1="12" y1="13" x2="9.5" y2="10.5" opacity="0.6" />
    <line x1="12" y1="15" x2="14.5" y2="12.5" opacity="0.6" />
  </svg>
);
const BuildingsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3.5" y="9" width="7" height="12" rx="0.6" />
    <rect x="13" y="4.5" width="7.5" height="16.5" rx="0.6" />
    <line x1="6" y1="12" x2="8" y2="12" opacity="0.6" />
    <line x1="6" y1="15.5" x2="8" y2="15.5" opacity="0.6" />
    <line x1="15.5" y1="8" x2="18" y2="8" opacity="0.6" />
    <line x1="15.5" y1="11.5" x2="18" y2="11.5" opacity="0.6" />
    <line x1="15.5" y1="15" x2="18" y2="15" opacity="0.6" />
  </svg>
);

// ═══════════════════════════════════════════════════════════════════════════
// Menu structure
// ═══════════════════════════════════════════════════════════════════════════
// Maps menu section IDs to the actual WEATHER_LAYERS keys they activate.
// "radar" in menu → "radar" in WEATHER_LAYERS (which is a composite).
const LAYERS_WITH_FUTURE = new Set(["radar", "wind", "temperature", "apparent_temperature", "dewpoint", "relative_humidity", "cloud_cover", "visibility"]);

interface SubItem {
  id: string;
  label: string;
  layerKey?: string;
  isOverlay?: boolean;
  overlayKey?: string;
  isToggle?: boolean;
  toggleKey?: string;
  // A "hide layer" toggle: shows/hides a data layer by flipping activeLayer
  // between hideLayerKey and null. Active/lit = layer currently HIDDEN. Used
  // for "Hide Precip" (hides the radar raster while overlays stay on).
  isLayerHide?: boolean;
  hideLayerKey?: string;
  // SPC Day 1 outlook product picks. Unlike overlays these are EXCLUSIVE —
  // the four products cover the same geography, so selecting one deselects
  // the others (state lives in the parent as a single spcOutlook value).
  isSpc?: boolean;
  spcKey?: SpcProduct;
}
interface MenuSection {
  id: string;
  label: string;
  icon: () => JSX.Element;
  defaultLayerKey?: string;
  layerKey?: string;
  subItems: SubItem[] | null;
  // Overlay-toggle sections: tapping the section icon toggles an overlay
  // on/off (like Tropical Systems) instead of setting the active data layer.
  overlayKey?: string;
  // SPC-default sections: tapping the section icon toggles the SPC outlook
  // on/off, defaulting to this product when turning on. Sub-items still
  // allow switching between products once the overlay is active.
  spcDefault?: SpcProduct;
}

const MENU: MenuSection[] = [
  { id: "radar", label: "Radar", icon: RadarIcon, defaultLayerKey: "radar", subItems: [
    { id: "hide_precip", label: "Hide Precip", isLayerHide: true, hideLayerKey: "radar" },
    { id: "lightning", label: "Lightning", isOverlay: true, overlayKey: "lightning" },
    { id: "warnings", label: "Severe Warnings (US)", isOverlay: true, overlayKey: "warnings" },
    { id: "alerts", label: "All Weather Alerts", isOverlay: true, overlayKey: "alerts" },
  ]},
  { id: "satellite", label: "Satellite", icon: SatelliteIcon, defaultLayerKey: "satellite_longwave", subItems: [
    { id: "satellite_longwave", label: "Infrared", layerKey: "satellite_longwave" },
    { id: "satellite_shortwave", label: "Visible", layerKey: "satellite_shortwave" },
    { id: "satellite_water_vapor", label: "Water Vapor", layerKey: "satellite_water_vapor" },
  ]},
  { id: "sky", label: "Sky", icon: SkyIcon, defaultLayerKey: "cloud_cover", subItems: [
    { id: "cloud_cover", label: "Cloud Cover", layerKey: "cloud_cover" },
    { id: "visibility", label: "Visibility", layerKey: "visibility" },
  ]},
  { id: "air", label: "Air", icon: AirIcon, defaultLayerKey: "temperature", subItems: [
    { id: "temperature", label: "Temperature", layerKey: "temperature" },
    { id: "apparent_temperature", label: "Apparent Temp", layerKey: "apparent_temperature" },
    { id: "relative_humidity", label: "Relative Humidity", layerKey: "relative_humidity" },
    { id: "dewpoint", label: "Dewpoint", layerKey: "dewpoint" },
  ]},
  { id: "wind", label: "Wind", icon: WindIcon, subItems: null, layerKey: "wind" },
  // Overlay-only sections: no defaultLayerKey/layerKey, so tapping the icon
  // never switches the active data layer — these draw on top of whatever base
  // layer is showing. (In the collapsed rail, a single tap on a section with
  // no defaultLayerKey is a no-op + hint; double-tap expands, same as any
  // other section.)
  { id: "outlook", label: "Storm Outlook (US)", icon: StormOutlookIcon, spcDefault: "day1", subItems: [
    { id: "spc_day1", label: "Today", isSpc: true, spcKey: "day1" },
    { id: "spc_day2", label: "Tomorrow", isSpc: true, spcKey: "day2" },
    { id: "spc_day3", label: "Next Day", isSpc: true, spcKey: "day3" },
  ]},
  { id: "tropical", label: "Tropical Systems", icon: TropicalIcon, subItems: null, overlayKey: "tropical" },
];

// ═══════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════
function AnimatedDropdown({ open, children }: { open: boolean; children: React.ReactNode }) {
  const innerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  useEffect(() => { if (innerRef.current) setHeight(innerRef.current.scrollHeight); }, [children, open]);
  return (
    <div style={{ overflow: "hidden", transition: "max-height 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.2s ease", maxHeight: open ? height : 0, opacity: open ? 1 : 0 }}>
      <div ref={innerRef}>{children}</div>
    </div>
  );
}

function Tooltip({ text, visible }: { text: string; visible: boolean }) {
  const t = useTheme();
  return (
    <div style={{
      position: "absolute", right: "100%", top: "50%",
      transform: `translateY(-50%) translateX(${visible ? "-8px" : "-4px"})`,
      background: t.panelBg, border: `1px solid ${t.panelBorder}`,
      borderRadius: 5, padding: "5px 10px", fontSize: 12, color: t.textPrimary,
      whiteSpace: "nowrap", pointerEvents: "none", opacity: visible ? 1 : 0,
      transition: "opacity 0.15s ease, transform 0.15s ease",
      boxShadow: t.panelShadow, zIndex: 999, fontWeight: 500, letterSpacing: 0.2,
    }}>{text}</div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Props interface — everything the parent (App.tsx) needs to pass in
// ═══════════════════════════════════════════════════════════════════════════
export interface StormChanceUIProps {
  // Layer selection
  activeLayer: string | null;
  setActiveLayer: (layer: string | null) => void;
  // "Hide Precip": hides the radar raster while radar stays active.
  hidePrecip: boolean;
  setHidePrecip: (v: boolean) => void;
  // Map flavor (basemap theme)
  mapFlavor: string;
  setMapFlavor: (flavor: string) => void;
  // Settings: Protomaps label size multiplier (1 = default)
  labelSize: number;
  setLabelSize: (v: number) => void;
  // Settings: basemap feature visibility toggles
  featureToggles: FeatureToggles;
  setFeatureToggle: (key: keyof FeatureToggles, value: boolean) => void;
  // Settings: weather layer opacity (0–100)
  opacity: number;
  setOpacity: (v: number) => void;
  // Overlays
  lightningEnabled: boolean;
  setLightningEnabled: (v: boolean) => void;
  warningsEnabled: boolean;
  setWarningsEnabled: (v: boolean) => void;
  alertsEnabled: boolean;
  setAlertsEnabled: (v: boolean) => void;
  tropicalEnabled: boolean;
  setTropicalEnabled: (v: boolean) => void;
  // SPC Day 1 product — exclusive pick; null = off.
  spcOutlook: SpcProduct | null;
  setSpcOutlook: (v: SpcProduct | null) => void;
  // Scrubber / playback
  keyframes: any[];        // representative keyframe list for timestamp display
  scrubberLength: number;  // total scrubber range
  currentIdx: number;
  onScrub: (v: number) => void;
  playing: boolean;
  setPlaying: (v: boolean) => void;
  loadedCount: number;
  // ── Props app.tsx passes that this component doesn't currently read ──
  // These were being passed at the call site but never declared, which is what
  // produced the TS2322 on <StormChanceUI ... />. They look like leftovers from
  // when the timeline was a separate component; nothing in here references them
  // today. Optional so the call site typechecks with zero behaviour change —
  // safe to delete from BOTH this interface and the app.tsx call site whenever
  // you want to clean them up.
  length?: number;
  oldestLoaded?: number | null;
  total?: number;
  totalExpectedFrames: number;
  /** Frames that permanently failed to decode (after retry). Counted toward
   * the bar's COMPLETION check — not its percentage — so a partial failure
   * can't leave the progress UI pinned below 100% forever, and is surfaced
   * as an "N unavailable" note instead of looking like a stall. */
  failedCount?: number;
  // Map ref for zoom/compass/geolocation
  mapRef: React.MutableRefObject<any>;
  // Loading indicator
  loading: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// Legend builder — reads palette from weatherLayerConfig
// ═══════════════════════════════════════════════════════════════════════════
function buildLegendItems(layerKey: string | null) {
  if (!layerKey) return [];
  const cfg = WEATHER_LAYERS[layerKey];
  if (!cfg) return [];

  // For composite layers, check if all sub-layers share precipType palettes
  if (cfg.composite && cfg.composite.length > 0) {
    const subs = cfg.composite.map((k: string) => WEATHER_LAYERS[k]).filter(Boolean);
    const allPrecipType = subs.length > 0 && subs.every((s: LayerConfig) => !!s.precipTypePalettes);
    if (allPrecipType) {
      const sub = subs[0];
      return buildPrecipLegend(sub);
    }
    // Otherwise use first sub-layer's palette
    return buildSingleLegend(WEATHER_LAYERS[cfg.composite[0]], cfg.composite[0]);
  }

  if (cfg.precipTypePalettes) return buildPrecipLegend(cfg);
  return buildSingleLegend(cfg, layerKey);
}

function buildPrecipLegend(cfg: LayerConfig) {
  const makeGradient = (stops: any[]) =>
    `linear-gradient(to right, ${stops.map((s: any) => `rgb(${s[1][0]},${s[1][1]},${s[1][2]}) ${s[0] * 100}%`).join(", ")})`;

  return [
    { label: "RAIN", colorKey: "accent" as const, gradient: makeGradient(cfg.precipTypePalettes!.rain), low: "Light", high: "Heavy" },
    { label: "SLEET", colorKey: null, color: "#e879a8", gradient: makeGradient(cfg.precipTypePalettes!.sleet), low: "Light", high: "Heavy" },
    { label: "SNOW", colorKey: null, color: "#a0a0ff", gradient: makeGradient(cfg.precipTypePalettes!.snow), low: "Light", high: "Heavy" },
  ];
}

// Display label overrides for legends (config labels may differ from desired UI labels)
const LEGEND_LABEL_OVERRIDES: Record<string, string> = {
  "satellite_longwave": "INFRARED SATELLITE",
  "satellite_shortwave": "VISIBLE SATELLITE",
  "satellite_water_vapor": "WATER VAPOR SATELLITE",
};

function buildSingleLegend(cfg: LayerConfig, layerKey?: string) {
  const stops = cfg.palette.filter((s: any) => s[1][3] > 0);
  if (stops.length === 0) return [];
  const gradient = `linear-gradient(to right, ${stops.map((s: any) => `rgb(${s[1][0]},${s[1][1]},${s[1][2]}) ${s[0] * 100}%`).join(", ")})`;
  const labels = cfg.legendLabels;
  const displayLabel = (layerKey && LEGEND_LABEL_OVERRIDES[layerKey]) || cfg.label.toUpperCase();
  // `labels` carries every tick (e.g. the 7 evenly-spaced temperature/wind
  // values); the render block spaces them across the bar. low/high are kept as
  // a fallback for any consumer that only reads the endpoints.
  return [{ label: displayLabel, colorKey: "accent" as const, gradient, labels, low: labels[0], high: labels[labels.length - 1] }];
}

// ═══════════════════════════════════════════════════════════════════════════
// Bottom pill responsive tiers
// ═══════════════════════════════════════════════════════════════════════════
// The timeline + legend pill is the widest piece of chrome on the map, so the
// single `isSmall` cliff (<400px) the rest of the UI uses is not enough for it.
// Between a full desktop window and a phone there is a long stretch — roughly
// 400-760px: a browser dragged narrow, split-screen, tablet portrait, phone
// landscape — where the old sizing kept a hard 440px minimum plus three 100px
// legend columns (376px of legend alone) and simply overflowed the viewport,
// pushing the RAIN/SLEET/SNOW bars off the edge of the pill.
//
// These tiers step the pill down gradually instead. Everything shrinks first —
// paddings, fonts, the play button, the legend columns, the number of tick
// labels — and content is only DROPPED (PAST/FUTURE labels, the long date in
// the time readout) once there is genuinely no room for it. The legend itself
// now survives all the way down to phone widths in a micro form, rather than
// vanishing below 400px as it used to.
export type PillTier = "lg" | "md" | "sm" | "xs";

function computePillTier(w: number, h: number): PillTier {
  // xs is deliberately the same test as the global `isSmall` so the pill and
  // the rest of the mobile chrome flip to phone mode at the same instant.
  if (w < 400 || h < 400) return "xs";
  if (w < 560) return "sm"; // narrow window / phone landscape
  if (w < 760) return "md"; // tablet portrait / split-screen
  return "lg";              // full desktop — unchanged from the original sizing
}

interface PillMetrics {
  /** Outer wrapper width / max width. Below `md` the pill spans the viewport. */
  outerWidth: string;
  outerMaxWidth: string;
  bottom: number;
  radius: number;
  /** Inner min width. 0 lets the pill track the viewport on small screens. */
  minWidth: number;
  // Scrubber row
  rowPad: string;
  rowGap: number;
  playSize: number;
  playRadius: number;
  playFont: number;
  scrubberH: number;
  thumb: number;
  tickH: number;
  timeFont: number;
  timeMinW: number;
  /** Drop the MM/DD suffix from the time readout to buy horizontal room. */
  shortTime: boolean;
  /** PAST / FUTURE captions above the radar scrubber. */
  showPastFuture: boolean;
  // Preload bar
  progressPad: string;
  // Legend
  legendPad: string;
  legendGap: number;
  legendMinW: number;
  legendLabelFont: number;
  legendLabelSpacing: number;
  legendBarH: number;
  legendTickFont: number;
  /** Max tick labels under a gradient; extras are thinned out evenly. */
  maxTicks: number;
  /** Whether the gradient legend renders at all (true on every tier now). */
  showLegend: boolean;
  /** Use the abbreviated layer name (IR SAT vs INFRARED SATELLITE). */
  shortLegendLabel: boolean;
  // SPC severity legend
  spcPad: string;
  spcTitleFont: number;
  spcGap: number;
  spcSwatch: number;
  spcFont: number;
  spcShortLabels: boolean;
}

const PILL_METRICS: Record<PillTier, PillMetrics> = {
  lg: {
    outerWidth: "auto", outerMaxWidth: "calc(100vw - 40px)", bottom: 16, radius: 10, minWidth: 440,
    rowPad: "10px 16px", rowGap: 12, playSize: 32, playRadius: 7, playFont: 13,
    scrubberH: 18, thumb: 14, tickH: 14, timeFont: 12, timeMinW: 130,
    shortTime: false, showPastFuture: true,
    progressPad: "0 16px 6px",
    legendPad: "7px 20px 6px", legendGap: 18, legendMinW: 100,
    legendLabelFont: 10, legendLabelSpacing: 1.5, legendBarH: 7, legendTickFont: 8,
    maxTicks: 7, showLegend: true, shortLegendLabel: false,
    spcPad: "7px 20px 7px", spcTitleFont: 10, spcGap: 14, spcSwatch: 11, spcFont: 9.5, spcShortLabels: false,
  },
  md: {
    outerWidth: "auto", outerMaxWidth: "calc(100vw - 32px)", bottom: 14, radius: 10, minWidth: 380,
    rowPad: "9px 13px", rowGap: 10, playSize: 30, playRadius: 7, playFont: 12,
    scrubberH: 18, thumb: 13, tickH: 13, timeFont: 11.5, timeMinW: 116,
    shortTime: false, showPastFuture: true,
    progressPad: "0 13px 5px",
    legendPad: "6px 13px 6px", legendGap: 12, legendMinW: 78,
    legendLabelFont: 9.5, legendLabelSpacing: 1.1, legendBarH: 6, legendTickFont: 7.5,
    maxTicks: 5, showLegend: true, shortLegendLabel: false,
    spcPad: "6px 13px 6px", spcTitleFont: 9.5, spcGap: 10, spcSwatch: 10, spcFont: 9, spcShortLabels: false,
  },
  sm: {
    outerWidth: "calc(100vw - 28px)", outerMaxWidth: "calc(100vw - 28px)", bottom: 12, radius: 9, minWidth: 0,
    rowPad: "8px 11px", rowGap: 9, playSize: 28, playRadius: 6, playFont: 12,
    scrubberH: 17, thumb: 12, tickH: 12, timeFont: 11, timeMinW: 64,
    shortTime: true, showPastFuture: false,
    progressPad: "0 11px 5px",
    legendPad: "5px 11px 5px", legendGap: 8, legendMinW: 54,
    legendLabelFont: 8.5, legendLabelSpacing: 0.8, legendBarH: 5, legendTickFont: 7,
    maxTicks: 3, showLegend: true, shortLegendLabel: true,
    spcPad: "5px 11px 5px", spcTitleFont: 8.5, spcGap: 7, spcSwatch: 9, spcFont: 8, spcShortLabels: true,
  },
  xs: {
    outerWidth: "calc(100vw - 24px)", outerMaxWidth: "calc(100vw - 24px)", bottom: 8, radius: 8, minWidth: 0,
    rowPad: "6px 10px", rowGap: 8, playSize: 26, playRadius: 6, playFont: 11,
    scrubberH: 16, thumb: 12, tickH: 12, timeFont: 10, timeMinW: 56,
    shortTime: true, showPastFuture: false,
    progressPad: "0 10px 4px",
    legendPad: "4px 9px 5px", legendGap: 6, legendMinW: 44,
    legendLabelFont: 7.5, legendLabelSpacing: 0.5, legendBarH: 4.5, legendTickFont: 6.5,
    maxTicks: 2, showLegend: true, shortLegendLabel: true,
    spcPad: "5px 9px 5px", spcTitleFont: 8, spcGap: 6, spcSwatch: 9, spcFont: 8, spcShortLabels: true,
  },
};

// Abbreviations used once the legend column is too narrow for the full name.
// Only the genuinely long ones need an entry — anything absent falls through
// to the full label, which ellipsizes if it still doesn't fit.
const LEGEND_SHORT_LABELS: Record<string, string> = {
  "INFRARED SATELLITE": "IR SAT",
  "VISIBLE SATELLITE": "VIS SAT",
  "WATER VAPOR SATELLITE": "WV SAT",
  "PRECIPITATION": "PRECIP",
  "TEMPERATURE": "TEMP",
};

// Evenly thin a tick-label array down to `maxTicks`, always keeping the first
// and last so the gradient's endpoints stay labelled. A 7-stop temperature
// scale reads fine at 100px but turns into overlapping mush at 44px, so the
// narrow tiers show 3 values, then just the endpoints.
function thinTicks(labels: string[], maxTicks: number): string[] {
  if (labels.length <= maxTicks) return labels;
  if (maxTicks <= 2) return [labels[0], labels[labels.length - 1]];
  const step = (labels.length - 1) / (maxTicks - 1);
  const out: string[] = [];
  for (let i = 0; i < maxTicks; i++) out.push(labels[Math.round(i * step)]);
  return out;
}

// ═══════════════════════════════════════════════════════════════════════════
// Main exported component
// ═══════════════════════════════════════════════════════════════════════════
export function StormChanceUI(props: StormChanceUIProps) {
  const [isDark, setIsDark] = useState(getIsNight);
  const userOverrodeTheme = useRef(false);
  const theme = isDark ? THEMES.dark : THEMES.light;
  useEffect(() => {
    const iv = setInterval(() => {
      if (!userOverrodeTheme.current) setIsDark(getIsNight());
    }, 60000);
    return () => clearInterval(iv);
  }, []);
  const toggleTheme = () => {
    userOverrodeTheme.current = true;
    setIsDark((d) => !d);
  };

  // ─── Viewport measurement ────────────────────────────────────────────────
  // MEASURING IS THE HARD PART, not the thresholds, and there are THREE
  // distinct contexts this component renders in — each of which breaks a
  // different "obvious" signal:
  //
  //   (a) Fullscreen browser tab — everything agrees.
  //   (b) Fullscreen iOS in-app WKWebView — while the sheet animates in, the
  //       webview can report a near-zero height, and a native frame change
  //       does NOT reliably emit a JS `resize`. A single measurement at mount
  //       LATCHES that bogus value and every piece of chrome gated on it
  //       (timeline, legend, map controls) stays hidden all session.
  //   (c) Embedded card — the map is a ~250px-tall box inside a much taller
  //       scrolling weather screen. Here EVERY window-level signal
  //       (innerHeight, visualViewport, documentElement) describes the tall
  //       PAGE and none can see the short card, so the icon rail never
  //       collapses to its logo-only form.
  //
  // So we take every available reading INCLUDING the box this chrome is
  // actually positioned inside (which is the only signal correct in case c),
  // discard any individual reading that is implausibly tiny (case b), and use
  // the SMALLEST surviving width and height. Every signal measures the same
  // physical box from a different vantage point, so the true constraint is
  // always among the smallest honest readings, and an inflated or glitched
  // outlier can never win a minimum. If nothing is measurable, fall back to a
  // roomy default: an unmeasurable viewport must never be treated as tiny,
  // because hiding chrome looks broken while cramped chrome merely looks tight.

  // Readings below this are artifacts, not real devices or real embed boxes.
  const PLAUSIBLE_MIN = 200;
  const isPlausible = (v: { w: number; h: number }) => v.w >= PLAUSIBLE_MIN && v.h >= PLAUSIBLE_MIN;

  // The box this UI lives in. Populated from the chrome's positioned ancestor
  // (the map container) and kept current by a ResizeObserver.
  const boxSizeRef = useRef<{ w: number; h: number } | null>(null);
  const chromeAnchorRef = useRef<HTMLDivElement>(null);

  const measureViewport = () => {
    if (typeof window === "undefined") return { w: 1024, h: 768 };
    const vv = window.visualViewport;
    const de = document.documentElement;
    const candidates: { w: number; h: number }[] = [];
    if (boxSizeRef.current) candidates.push(boxSizeRef.current);
    if (vv) candidates.push({ w: Math.round(vv.width), h: Math.round(vv.height) });
    candidates.push({ w: Math.round(window.innerWidth), h: Math.round(window.innerHeight) });
    if (de) candidates.push({ w: Math.round(de.clientWidth), h: Math.round(de.clientHeight) });
    const plausible = candidates.filter(isPlausible);
    if (plausible.length === 0) return { w: 1024, h: 768 };
    return {
      w: Math.min(...plausible.map((c) => c.w)),
      h: Math.min(...plausible.map((c) => c.h)),
    };
  };

  // Responsive: detect small screens (<400px either dimension)
  const [isSmall, setIsSmall] = useState(() => {
    const v = measureViewport();
    return isPlausible(v) ? (v.w < 400 || v.h < 400) : false;
  });
  // Very short viewports hide chrome that cannot fit. TWO conditions, not one:
  // a phone in LANDSCAPE is short (height ~320-430) but WIDE, and a flat
  // height test hid the timeline there too — the very viewport a horizontal
  // scrubber suits best. Requiring "short AND narrow" limits this to genuinely
  // tiny viewports. Defaults to FALSE on an implausible measurement.
  const SHORT_H = 380;
  const TINY_W = 480;
  const [isVeryShort, setIsVeryShort] = useState(() => {
    const v = measureViewport();
    return isPlausible(v) ? (v.h <= SHORT_H && v.w < TINY_W) : false;
  });
  // Graduated width tier for the bottom timeline/legend pill only. See
  // computePillTier — the pill needs intermediate steps between desktop and
  // phone that the binary isSmall flag can't express.
  const [pillTier, setPillTier] = useState<PillTier>(() => {
    const v = measureViewport();
    return isPlausible(v) ? computePillTier(v.w, v.h) : "xs";
  });
  useEffect(() => {
    const check = () => {
      const v = measureViewport();
      if (!isPlausible(v)) return; // discard artifacts instead of latching them
      setIsSmall(v.w < 400 || v.h < 400);
      setIsVeryShort(v.h <= SHORT_H && v.w < TINY_W);
      setPillTier(computePillTier(v.w, v.h));
    };

    // Measure the box this chrome is positioned inside. `offsetParent` is the
    // nearest positioned ancestor — the map wrapper in both the fullscreen and
    // embedded-card layouts — and is the ONLY reading that sees an embedded
    // card's real height.
    const anchor = chromeAnchorRef.current;
    const host = (anchor?.offsetParent as HTMLElement | null) ?? anchor?.parentElement ?? null;
    const readBox = () => {
      if (!host) { check(); return; }
      const r = host.getBoundingClientRect();
      const next = { w: Math.round(r.width), h: Math.round(r.height) };
      const prev = boxSizeRef.current;
      if (!prev || prev.w !== next.w || prev.h !== next.h) boxSizeRef.current = next;
      check();
    };
    readBox();

    // Several independent triggers, because none is dependable alone in a
    // webview: `resize` may never fire for a native frame change,
    // visualViewport events cover keyboard/zoom/sheet transitions, the
    // ResizeObservers catch layout changes that emit no event at all, pageshow
    // covers bfcache restores, and the delayed re-checks cover a present
    // animation that finishes after mount with no event of any kind.
    window.addEventListener("resize", readBox);
    window.addEventListener("orientationchange", readBox);
    window.visualViewport?.addEventListener("resize", readBox);
    window.visualViewport?.addEventListener("scroll", readBox);
    window.addEventListener("pageshow", readBox);

    let docRo: ResizeObserver | undefined;
    let boxRo: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      if (document.documentElement) {
        docRo = new ResizeObserver(readBox);
        docRo.observe(document.documentElement);
      }
      if (host) {
        boxRo = new ResizeObserver(readBox);
        boxRo.observe(host);
      }
    }
    const raf = requestAnimationFrame(readBox);
    const timers = [120, 400, 1000, 2500].map((ms) => setTimeout(readBox, ms));

    return () => {
      window.removeEventListener("resize", readBox);
      window.removeEventListener("orientationchange", readBox);
      window.visualViewport?.removeEventListener("resize", readBox);
      window.visualViewport?.removeEventListener("scroll", readBox);
      window.removeEventListener("pageshow", readBox);
      docRo?.disconnect();
      boxRo?.disconnect();
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Metrics for the current tier — every size in the bottom pill reads from
  // this one object so the tiers stay internally consistent.
  const M = PILL_METRICS[pillTier];

  const [expanded, setExpanded] = useState(false);
  // On small screens: logo-only → collapsed icons. Never full expanded.
  const [showIconPill, setShowIconPill] = useState(!isSmall);
  const effectiveExpanded = isSmall ? false : expanded;
  const [expandedSection, setExpandedSection] = useState<string | null>("radar");
  const [overlays, setOverlays] = useState({ lightning: props.lightningEnabled, warnings: props.warningsEnabled, alerts: props.alertsEnabled, tropical: props.tropicalEnabled });
  const [toggles, setToggles] = useState({ hidePrecip: false });
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [tooltipId, setTooltipId] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [expandHint, setExpandHint] = useState(false);
  const expandHintTimer = useRef<any>(null);

  // ── Small-screen long-press flyout ────────────────────────────────────────
  // On small viewports the full expanded menu is unreachable (the logo button
  // only toggles the icon rail), so a section's sub-items had no entry point.
  // Holding an eligible icon in the rail for LONG_PRESS_MS opens a mini menu
  // anchored to the LEFT of that icon.
  //
  // ELIGIBILITY — a section arms the timer if it has MORE THAN ONE sub-item,
  // COUNTING LOCKED ONES.
  //
  // This previously filtered locked rows out, which meant a premium sub-item
  // was invisible in the free build rather than padlocked: Satellite collapsed
  // to just Infrared (Visible and Water Vapor disappeared entirely) and Air to
  // just Temperature (Apparent Temp, Humidity, Dewpoint disappeared), and
  // neither section opened a flyout at all. That hid the entire premium
  // surface on mobile — a user cannot want what they cannot see — and it
  // contradicted the desktop dropdown, which has always shown locked rows
  // dimmed with a padlock.
  //
  // Locked rows are now SHOWN with a padlock (the windy.com treatment):
  // dimmed, still tappable, and a tap opens the upsell instead of changing the
  // layer. The row renderer further down already implements exactly this — see
  // `isLocked` there — so this only had to stop filtering them away.
  const LONG_PRESS_MS = 450;
  const subItemLocked = (section: MenuSection, sub: SubItem) =>
    isSectionLocked(section.id)
    || (sub.layerKey ? isLayerLocked(sub.layerKey) : false)
    || (sub.isOverlay ? isOverlayLocked(sub.overlayKey!) : false);
  const flyoutSubItems = (section: MenuSection) => section.subItems ?? [];
  const hasFlyout = (section: MenuSection) => flyoutSubItems(section).length > 1;
  /** A section that is itself USABLE but contains at least one premium
   * sub-item (free Satellite holding premium Visible / Water Vapor; free Air
   * holding Dewpoint etc.). Distinct from isSectionLocked, which means the
   * whole section is off limits. These get a smaller, dimmer padlock on the
   * rail icon so the pill advertises that there is premium content inside
   * without implying the section itself is unavailable. */
  const hasLockedSubItem = (section: MenuSection) =>
    !isSectionLocked(section.id)
    && (section.subItems ?? []).some((sub) => subItemLocked(section, sub));

  // `flyout` holds the open section id plus the icon's viewport top, so the
  // panel can be `position: fixed` and escape the pill's `overflow: hidden`.
  const [flyout, setFlyout] = useState<{ sectionId: string; top: number } | null>(null);
  const pressTimerRef = useRef<any>(null);
  // Set when a long press fires so the click that follows the release is
  // swallowed instead of also toggling the layer.
  const longPressFiredRef = useRef(false);

  const cancelLongPress = () => {
    clearTimeout(pressTimerRef.current);
    pressTimerRef.current = null;
  };

  const startLongPress = (section: MenuSection, el: HTMLElement | null) => {
    if (!hasFlyout(section)) return;
    cancelLongPress();
    longPressFiredRef.current = false;
    const rect = el?.getBoundingClientRect();
    pressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      setFlyout({ sectionId: section.id, top: rect ? rect.top : 80 });
      // Haptic confirmation where supported — the press has no visual "armed"
      // state, so this is the cue that the hold registered.
      try { (navigator as any).vibrate?.(12); } catch {}
    }, LONG_PRESS_MS);
  };

  // Close the flyout whenever the rail itself closes or the viewport grows out
  // of small mode, so it can't linger detached from its anchor.
  useEffect(() => {
    if (!isSmall || !showIconPill) setFlyout(null);
  }, [isSmall, showIconPill]);
  useEffect(() => {
    if (!flyout) return;
    const close = () => setFlyout(null);
    window.addEventListener("resize", close);
    return () => window.removeEventListener("resize", close);
  }, [flyout]);
  useEffect(() => () => cancelLongPress(), []);

  // Sync overlay state to parent
  useEffect(() => { props.setLightningEnabled(overlays.lightning); }, [overlays.lightning]);
  useEffect(() => { props.setWarningsEnabled(overlays.warnings); }, [overlays.warnings]);
  useEffect(() => { props.setAlertsEnabled(overlays.alerts); }, [overlays.alerts]);
  useEffect(() => { props.setTropicalEnabled(overlays.tropical); }, [overlays.tropical]);

  // Sync parent → local. The parent forces all overlays off whenever the
  // active layer isn't radar (lightning/warnings/alerts are radar-only). When
  // that happens the booleans in props flip to false; mirror them into the
  // local overlay state so the menu buttons de-light immediately instead of
  // staying lit against a now-removed map layer. Guarded so it only writes on
  // an actual divergence (avoids an update loop with the UI→parent effects).
  useEffect(() => {
    setOverlays((p) => {
      if (p.lightning === props.lightningEnabled
        && p.warnings === props.warningsEnabled
        && p.alerts === props.alertsEnabled
        && p.tropical === props.tropicalEnabled) return p;
      return { lightning: props.lightningEnabled, warnings: props.warningsEnabled, alerts: props.alertsEnabled, tropical: props.tropicalEnabled };
    });
  }, [props.lightningEnabled, props.warningsEnabled, props.alertsEnabled, props.tropicalEnabled]);

  // Lightning/warnings/alerts are radar companions: toggling them is a no-op
  // on any other layer (their buttons also render disabled there), so a
  // non-radar layer can never switch them back on. The Tropical Systems
  // overlays (hurricanes/typhoons) are NOT radar-bound — they live in their
  // own section and can draw over any base layer.
  const RADAR_ONLY_OVERLAYS = new Set(["lightning", "warnings", "alerts"]);
  const toggleOverlay = (key: string) => {
    // FREE TIER: premium overlays never toggle on — every toggle path (sub-item
    // buttons, collapsed-rail taps, mobile pill) funnels through here, so this
    // one guard covers them all. The menu rows also render these locked.
    if (isOverlayLocked(key)) {
      requestUpsell(key === "lightning" ? "Lightning" : "This overlay");
      return;
    }
    if (RADAR_ONLY_OVERLAYS.has(key) && props.activeLayer !== "radar") return;
    setOverlays((p: any) => ({ ...p, [key]: !p[key] }));
  };
  const toggleToggle = (key: string) => setToggles((p: any) => ({ ...p, [key]: !p[key] }));

  const showExpandHintFn = () => {
    setExpandHint(true);
    clearTimeout(expandHintTimer.current);
    expandHintTimer.current = setTimeout(() => setExpandHint(false), 2000);
  };

  // Double-tap detection
  const lastTapRef = useRef<Record<string, number>>({});
  const tapTimerRef = useRef<Record<string, any>>({});
  const DOUBLE_TAP_MS = 300;

  const handleCollapsedIconClick = (section: MenuSection) => {
    // FREE TIER: a fully-premium section (Sky) can't activate or expand from
    // the rail — single AND double tap both land on the upsell.
    if (isSectionLocked(section.id)) { requestUpsell(section.label); return; }
    if (!section.subItems) {
      if (section.overlayKey) toggleOverlay(section.overlayKey);
      else props.setActiveLayer(props.activeLayer === section.layerKey ? null : section.layerKey!);
      setExpandedSection(null);
      return;
    }
    const now = Date.now();
    const lastTap = lastTapRef.current[section.id] || 0;
    if (now - lastTap < DOUBLE_TAP_MS) {
      clearTimeout(tapTimerRef.current[section.id]);
      clearTimeout(expandHintTimer.current);
      setExpandHint(false);
      setExpanded(true);
      setExpandedSection(section.id);
      if (section.spcDefault && !props.spcOutlook) props.setSpcOutlook(section.spcDefault);
      if (section.defaultLayerKey) props.setActiveLayer(section.defaultLayerKey);
      lastTapRef.current[section.id] = 0;
    } else {
      lastTapRef.current[section.id] = now;
      tapTimerRef.current[section.id] = setTimeout(() => {
        if (section.spcDefault) props.setSpcOutlook(props.spcOutlook ? null : section.spcDefault);
        else if (section.defaultLayerKey) props.setActiveLayer(section.defaultLayerKey);
        setExpandedSection(null);
        showExpandHintFn();
      }, DOUBLE_TAP_MS);
    }
  };

  const isAnySectionItemActive = (section: MenuSection) => {
    if (section.layerKey && section.layerKey === props.activeLayer) return true;
    if (section.overlayKey && (overlays as any)[section.overlayKey]) return true;
    if (section.spcDefault && props.spcOutlook) return true;
    if (!section.subItems) return false;
    return section.subItems.some((sub) =>
      (sub.layerKey && sub.layerKey === props.activeLayer)
      || (sub.isOverlay && (overlays as any)[sub.overlayKey!])
      || (sub.isSpc && props.spcOutlook === sub.spcKey));
  };
  const isSectionLayerActive = (section: MenuSection) => {
    if (section.defaultLayerKey) return props.activeLayer === section.defaultLayerKey;
    if (section.layerKey) return props.activeLayer === section.layerKey;
    return false;
  };

  const t = theme;
  const COLLAPSED_W = 48, EXPANDED_W = 252;

  // Legend items from the active layer's config
  const legendItems = buildLegendItems(props.activeLayer);

  // Timeline: interpolate timestamp between adjacent keyframes for per-minute display
  const currentFloor = Math.min(Math.floor(props.currentIdx), Math.max(0, props.keyframes.length - 1));
  const currentCeil = Math.min(currentFloor + 1, props.keyframes.length - 1);
  const frac = props.currentIdx - currentFloor;
  const currentKf = props.keyframes[currentFloor];
  const nextKf = props.keyframes[currentCeil];
  let timeLabel = "";
  let timeLabelShort = "";
  const parseKfTime = (kf: any) => {
    if (!kf) return null;
    const fn = kf.filename || "";
    // Try YYYYMMDD_HHMMSS (most layers)
    let parts = fn.match(/(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})/);
    if (parts) return Date.UTC(+parts[1], +parts[2] - 1, +parts[3], +parts[4], +parts[5], +parts[6]);
    // Try YYYYMMDD_HHMM (no seconds)
    parts = fn.match(/(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})$/);
    if (!parts) parts = fn.match(/(\d{4})(\d{2})(\d{2})_(\d{2})_(\d{2})/);
    if (parts) return Date.UTC(+parts[1], +parts[2] - 1, +parts[3], +parts[4], +parts[5], 0);
    // Try YYYYMMDD_HH (hour only)
    parts = fn.match(/(\d{4})(\d{2})(\d{2})_(\d{2})$/);
    if (parts) return Date.UTC(+parts[1], +parts[2] - 1, +parts[3], +parts[4], 0, 0);
    // Last resort: try extracting any 8-digit date followed by any time-like digits
    parts = fn.match(/(\d{4})(\d{2})(\d{2})\D*(\d{2})\D*(\d{2})?/);
    if (parts) return Date.UTC(+parts[1], +parts[2] - 1, +parts[3], +parts[4], +(parts[5] || 0), 0);
    return null;
  };
  const t0 = parseKfTime(currentKf);
  const t1 = parseKfTime(nextKf);
  if (t0 != null) {
    // Interpolate between frame timestamps and round to nearest minute
    const interpMs = t1 != null && t1 !== t0 ? t0 + (t1 - t0) * frac : t0;
    const roundedMs = Math.round(interpMs / 60000) * 60000; // round to minute
    const d = new Date(roundedMs);
    // Convert UTC to local time for display
    const rawHour = d.getHours();
    const ampm = rawHour >= 12 ? "PM" : "AM";
    const h12 = rawHour % 12 || 12;
    const mi = String(d.getMinutes()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    timeLabel = `${h12}:${mi} ${ampm} ${mm}/${dd}`;
    timeLabelShort = `${h12}:${mi} ${ampm}`;
  }
  const isForecast = !!currentKf?.isForecast;
  const hasFuture = LAYERS_WITH_FUTURE.has(props.activeLayer || "");

  // Preload progress
  const failedFrames = props.failedCount ?? 0;
  const progressPct = props.totalExpectedFrames > 0 ? Math.round((props.loadedCount / props.totalExpectedFrames) * 100) : 0;
  const fullyLoaded = props.loadedCount + failedFrames >= props.totalExpectedFrames;

  // framesReady: the newly-selected layer has keyframes and a usable scrubber
  // range. While false (just after a layer switch, before frames arrive) we
  // keep the timeline panel mounted but show its scrubber in a disabled
  // "loading" state — so the panel never disappears and only its inner
  // contents refresh.
  const framesReady = props.scrubberLength > 0;

  // Map control helper
  const mapCtrlBtn = (onClick: () => void, children: React.ReactNode, isActive = false) => (
    <button onClick={onClick} style={{
      width: 38, height: 38, background: isActive ? t.accentMed : "none",
      border: "none", cursor: "pointer", color: isActive ? t.accent : t.textSecondary,
      display: "flex", alignItems: "center", justifyContent: "center",
      transition: "color 0.15s, background 0.15s",
    }}
      onMouseEnter={(e: any) => { if (!isActive) { e.currentTarget.style.color = t.accent; e.currentTarget.style.background = t.accentSoft; } }}
      onMouseLeave={(e: any) => { if (!isActive) { e.currentTarget.style.color = t.textSecondary; e.currentTarget.style.background = "none"; } }}
    >{children}</button>
  );

  const mapCtrlPill: React.CSSProperties = { background: t.panelBg, backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)" as any, border: `1px solid ${t.panelBorder}`, borderRadius: 10, boxShadow: t.panelShadow, overflow: "hidden" };

  // Shared settings dropdown body — title, basemap (Dark/Light), label-size
  // slider, feature toggles, layer-opacity slider. Rendered identically in the
  // desktop expanded menu and the mobile icon pill.
  const featureToggleDefs: { key: keyof FeatureToggles; label: string; icon: () => JSX.Element }[] = [
    { key: "roads", label: "Roads", icon: RoadsIcon },
    { key: "highways", label: "Highways", icon: HighwaysIcon },
    { key: "landuse", label: "Landuse", icon: LanduseIcon },
    { key: "buildings", label: "Buildings", icon: BuildingsIcon },
  ];
  const settingsPanelBody = (
    <>
      {/* ── Title ── */}
      <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, letterSpacing: 0.2, marginBottom: 2 }}>Map Settings</div>

      {/* ── Basemap (Dark / Light) ── */}
      <div>
        <div style={{ fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase", color: t.textMuted, fontWeight: 700, marginBottom: 8 }}>Basemap</div>
        <div style={{ display: "flex", gap: 6 }}>
          {[
            { id: "black", label: "Dark", preview: "#0a0a0a" },
            { id: "grayscale", label: "Light", preview: "#c8ccd1" },
          ].map((b) => (
            <button key={b.id} onClick={() => props.setMapFlavor(b.id)} style={{
              flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 5,
              padding: "7px 6px", borderRadius: 7, cursor: "pointer",
              background: props.mapFlavor === b.id ? t.activeBg : "transparent",
              border: `1px solid ${props.mapFlavor === b.id ? t.accentBorder : t.divider}`,
              transition: "all 0.15s ease",
            }}>
              <div style={{ width: "100%", height: 22, borderRadius: 4, background: b.preview, border: `1.5px solid ${props.mapFlavor === b.id ? t.accent : t.textDimmer}` }} />
              <span style={{ fontSize: 10, fontWeight: props.mapFlavor === b.id ? 600 : 400, color: props.mapFlavor === b.id ? t.accent : t.textMuted }}>{b.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Label size ── */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
          <span style={{ fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase", color: t.textMuted, fontWeight: 700 }}>Label size</span>
          <span style={{ fontSize: 10, color: t.textDim }}>{props.labelSize.toFixed(1)}×</span>
        </div>
        <input type="range" min={0.6} max={1.6} step={0.1} value={props.labelSize}
          onChange={(e) => props.setLabelSize(parseFloat(e.target.value))}
          style={{ width: "100%", accentColor: t.accent, cursor: "pointer" }} />
      </div>

      {/* ── Feature toggles (icon row of 4) ── */}
      <div>
        <div style={{ fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase", color: t.textMuted, fontWeight: 700, marginBottom: 8 }}>Map features</div>
        <div style={{ display: "flex", gap: 6 }}>
          {featureToggleDefs.map(({ key, label, icon: Icon }) => {
            const on = props.featureToggles[key];
            return (
              <button key={key} onClick={() => props.setFeatureToggle(key, !on)}
                aria-label={label} aria-pressed={on} title={`${label}: ${on ? "on" : "off"}`}
                style={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                  height: 36, borderRadius: 7, cursor: "pointer",
                  background: on ? t.accentBg : "transparent",
                  border: `1px solid ${on ? t.accentBorder : t.divider}`,
                  color: on ? t.accent : t.textMuted,
                  transition: "background 0.15s ease, border-color 0.15s ease, color 0.15s ease, opacity 0.15s ease",
                  opacity: on ? 1 : 0.7,
                }}
                onMouseEnter={(e: any) => { if (!on) { e.currentTarget.style.color = t.textPrimary; e.currentTarget.style.opacity = "1"; } }}
                onMouseLeave={(e: any) => { if (!on) { e.currentTarget.style.color = t.textMuted; e.currentTarget.style.opacity = "0.7"; } }}
              >
                <Icon />
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Layer opacity ── */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
          <span style={{ fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase", color: t.textMuted, fontWeight: 700 }}>Layer opacity</span>
          <span style={{ fontSize: 10, color: t.textDim }}>{props.opacity}%</span>
        </div>
        <input type="range" min={0} max={100} step={1} value={props.opacity}
          onChange={(e) => props.setOpacity(parseInt(e.target.value))}
          style={{ width: "100%", accentColor: t.accent, cursor: "pointer" }} />
      </div>
    </>
  );

  return (
    <ThemeContext.Provider value={theme}>
      {/* ─── Floating menu (top-right) ─────────────────────────────── */}
      {/* chromeAnchorRef is used only to locate the positioned ancestor this
          UI lives in, so the responsive tiers measure the MAP BOX rather than
          the surrounding page. See measureViewport. */}
      <div ref={chromeAnchorRef} style={{ position: "absolute", top: 14, right: 14, zIndex: 50 }}>
        {/* Expand hint — not on small screens */}
        {!isSmall && !expanded && expandHint && (
          <div style={{
            position: "absolute", right: "calc(100% + 8px)", top: 10,
            background: t.panelBg, backdropFilter: "blur(16px)",
            border: `1px solid ${t.accentBorder}`, borderRadius: 8, padding: "6px 12px",
            boxShadow: t.panelShadow, whiteSpace: "nowrap", animation: "hintFadeIn 0.2s ease-out",
          }}>
            <span style={{ fontSize: 11, color: t.accent, fontWeight: 500 }}>Tap to expand</span>
            <div style={{
              position: "absolute", right: -5, top: "50%", transform: "translateY(-50%) rotate(45deg)",
              width: 8, height: 8, background: t.panelBg,
              borderRight: `1px solid ${t.accentBorder}`, borderTop: `1px solid ${t.accentBorder}`,
            }} />
          </div>
        )}

        <div style={{
          width: isSmall
            ? (showIconPill ? 40 : 36)
            : (effectiveExpanded ? EXPANDED_W : COLLAPSED_W),
          transition: "width 0.3s cubic-bezier(0.4,0,0.2,1)",
          background: t.panelBg, backdropFilter: "blur(20px)",
          border: `1px solid ${t.panelBorder}`, borderRadius: 12, boxShadow: t.panelShadow,
          display: "flex", flexDirection: "column", overflow: "hidden",
          maxHeight: "calc(100vh - 28px)",
        }}>

          {/* ─── Small screen: logo-only header ─────────────────────── */}
          {isSmall && (
            <>
              <button onClick={() => setShowIconPill((s) => !s)} style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: "8px 0", background: "none", border: "none",
                borderBottom: showIconPill ? `1px solid ${t.divider}` : "none",
                cursor: "pointer", width: "100%", minHeight: 36,
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <BrandCube size={26} />
                </div>
              </button>
              {showIconPill && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "4px 0 6px", gap: 1 }}>
                  {MENU.map((section) => {
                    const isActive = isAnySectionItemActive(section) || isSectionLayerActive(section);
                    const Icon = section.icon;
                    return (
                      <div key={section.id} style={{ position: "relative" }}>
                        <button onClick={() => {
                          // A completed long press already opened the flyout;
                          // don't also run the tap action on release.
                          if (longPressFiredRef.current) { longPressFiredRef.current = false; return; }
                          // FREE TIER: fully-premium sections upsell instead.
                          if (isSectionLocked(section.id)) { requestUpsell(section.label); return; }
                          if (!section.subItems) {
                            if (section.overlayKey) toggleOverlay(section.overlayKey);
                            else props.setActiveLayer(props.activeLayer === section.layerKey ? null : section.layerKey!);
                          } else if (section.spcDefault) {
                            props.setSpcOutlook(props.spcOutlook ? null : section.spcDefault);
                          } else if (section.defaultLayerKey) {
                            props.setActiveLayer(section.defaultLayerKey);
                          }
                        }}
                          // Hold to open the sub-item flyout. Pointer events cover
                          // touch, pen and mouse in one path; any move/leave/cancel
                          // before the timer fires aborts it so a scroll-drag over
                          // the rail never opens the menu. No-op on sections with
                          // fewer than two reachable sub-items (see hasFlyout).
                          onPointerDown={(e: any) => startLongPress(section, e.currentTarget)}
                          onPointerUp={cancelLongPress}
                          onPointerLeave={cancelLongPress}
                          onPointerCancel={cancelLongPress}
                          // Suppress the OS text-selection / context menu that a
                          // long press otherwise triggers on mobile.
                          onContextMenu={(e: any) => { if (hasFlyout(section)) e.preventDefault(); }}
                          style={{ width: 30, height: 30, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", background: isActive ? t.accentMed : "transparent", border: "none", cursor: "pointer", color: isActive ? t.accent : t.iconInactive, position: "relative", opacity: isSectionLocked(section.id) ? 0.55 : 1, touchAction: "manipulation", WebkitTouchCallout: "none", WebkitUserSelect: "none", userSelect: "none" } as React.CSSProperties}>
                          <Icon />
                          {isSectionLocked(section.id) ? (
                            <div style={{ position: "absolute", top: 1, right: 1 }}><LockIcon size={8} /></div>
                          ) : hasLockedSubItem(section) ? (
                            // Section is usable but holds premium sub-items —
                            // a dimmer padlock hints there is more inside.
                            <div style={{ position: "absolute", top: 1, right: 1, opacity: 0.6 }}><LockIcon size={8} /></div>
                          ) : null}
                        </button>
                      </div>
                    );
                  })}
                  {/* Settings gear (mobile) */}
                  <div style={{ width: "70%", height: 1, background: t.divider, margin: "4px 0" }} />
                  <button onClick={() => setShowSettings((s) => !s)} aria-label="Settings" title="Settings"
                    style={{ width: 30, height: 30, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", background: showSettings ? t.accentMed : "transparent", border: "none", cursor: "pointer", color: showSettings ? t.accent : t.iconInactive }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
                    </svg>
                  </button>
                </div>
              )}
            </>
          )}
          {/* ─── Normal screen: full logo row ───────────────────────── */}
          {!isSmall && (
            <>
              <div style={{
                display: "flex", alignItems: "center",
                padding: effectiveExpanded ? "10px 10px 10px 16px" : "14px 0",
                justifyContent: effectiveExpanded ? "space-between" : "center",
                borderBottom: `1px solid ${t.divider}`, minHeight: 52,
              }}>
                <button onClick={() => setExpanded(!expanded)} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  background: "none", border: "none", cursor: "pointer", padding: 0,
                }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <BrandCube size={30} />
                  </div>
                  {effectiveExpanded && <BrandWordmark size={15} />}
                </button>
                {effectiveExpanded && (
              <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                <button onClick={toggleTheme} style={{
                  width: 30, height: 30, borderRadius: 7, background: "none", border: `1px solid ${t.divider}`,
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                  color: t.textMuted, transition: "all 0.15s ease", flexShrink: 0,
                }}>
                  {isDark ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
                      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                      <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
                      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
                    </svg>
                  )}
                </button>
                <div style={{ position: "relative" }}>
                  <button onClick={() => setShowSettings((s) => !s)} aria-label="Settings" title="Settings" style={{
                    width: 30, height: 30, borderRadius: 7,
                    background: showSettings ? t.accentSoft : "none",
                    border: `1px solid ${showSettings ? t.accentBorder : t.divider}`,
                    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                    color: showSettings ? t.accent : t.textMuted, transition: "all 0.15s ease",
                  }}>
                    {/* Gear icon */}
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Expanded menu — normal screens only */}
          {!isSmall && effectiveExpanded && (
            <div style={{ overflowY: "auto", padding: "6px 0 8px", flex: 1 }}>
              {MENU.map((section) => {
                const isExp = expandedSection === section.id;
                const isActive = isAnySectionItemActive(section) || isSectionLayerActive(section);
                const Icon = section.icon;
                const isStandalone = !section.subItems;
                return (
                  <div key={section.id} style={{ marginBottom: 1 }}>
                    <button onClick={() => {
                      // FREE TIER: fully-premium sections (Sky) never expand or
                      // activate — the header tap goes straight to the upsell.
                      if (isSectionLocked(section.id)) { requestUpsell(section.label); return; }
                      if (isStandalone) {
                        if (section.overlayKey) toggleOverlay(section.overlayKey);
                        else props.setActiveLayer(props.activeLayer === section.layerKey ? null : section.layerKey!);
                        setExpandedSection(null);
                      }
                      else if (isExp) { setExpandedSection(null); if (section.spcDefault && props.spcOutlook) props.setSpcOutlook(null); else if (section.defaultLayerKey && props.activeLayer === section.defaultLayerKey) props.setActiveLayer(null); }
                      else { setExpandedSection(section.id); if (section.spcDefault) props.setSpcOutlook(props.spcOutlook ? null : section.spcDefault); else if (section.defaultLayerKey) props.setActiveLayer(section.defaultLayerKey); }
                    }}
                      onMouseEnter={() => setHoveredItem(section.id)} onMouseLeave={() => setHoveredItem(null)}
                      style={{
                        width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "10px 16px",
                        background: isActive ? t.activeBg : hoveredItem === section.id ? t.hoverBg : "transparent",
                        border: "none", cursor: "pointer", transition: "background 0.15s ease",
                        borderRight: isActive ? `2px solid ${t.accent}` : "2px solid transparent",
                        opacity: isSectionLocked(section.id) ? 0.55 : 1,
                      }}>
                      <div style={{ color: isActive ? t.accent : t.textMuted, display: "flex", alignItems: "center", flexShrink: 0 }}><Icon /></div>
                      <span style={{ flex: 1, textAlign: "left", fontSize: 13.5, fontWeight: isActive ? 600 : 500, color: isActive ? t.textPrimary : t.textSecondary }}>{section.label}</span>
                      {isSectionLocked(section.id) ? <LockIcon /> : (!isStandalone && <ChevronDown open={isExp} color={t.chevron} />)}
                    </button>
                    {!isStandalone && (
                      <AnimatedDropdown open={isExp}>
                        <div style={{ padding: "2px 0 6px" }}>
                          {section.subItems!.map((sub) => {
                            // "Hide Precip": independent flag (props.hidePrecip).
                            // Lit when precip is hidden. Only meaningful when its
                            // target layer (radar) is the active layer; shown
                            // disabled otherwise so it never looks broken.
                            const hideTargetActive = sub.isLayerHide ? props.activeLayer === sub.hideLayerKey : false;
                            const isSubActive = sub.isOverlay ? (overlays as any)[sub.overlayKey!]
                              : sub.isSpc ? props.spcOutlook === sub.spcKey
                              : sub.isLayerHide ? props.hidePrecip
                              : sub.isToggle ? (toggles as any)[sub.toggleKey!]
                              : props.activeLayer === sub.layerKey;
                            // Radar-companion overlays (lightning / warnings /
                            // alerts) only apply over radar and render disabled
                            // elsewhere; "Hide Precip" only when radar is active.
                            // Combination rules (SPC solo; tropical only with
                            // wind) are enforced by the parent's selection
                            // wrappers in App.tsx — the newest pick simply wins
                            // — so nothing here needs disabling for them.
                            const isDisabled = sub.isLayerHide ? !hideTargetActive
                              : sub.isOverlay ? (RADAR_ONLY_OVERLAYS.has(sub.overlayKey!) && props.activeLayer !== "radar")
                              : false;
                            // FREE TIER: premium sub-items render dimmed with a
                            // padlock and tap straight to the upsell toast. Kept
                            // VISIBLE (not hidden) on purpose — the locked row IS
                            // the ad for premium. Distinct from isDisabled, which
                            // is situational (radar-only overlays off-radar) and
                            // carries no lock affordance.
                            const isLocked = isSectionLocked(section.id)
                              || (sub.layerKey ? isLayerLocked(sub.layerKey) : false)
                              || (sub.isOverlay ? isOverlayLocked(sub.overlayKey!) : false);
                            let subIcon: JSX.Element | null = null, accentColor = t.accent;
                            if (sub.overlayKey === "lightning") { subIcon = <LightningIcon />; accentColor = "#ffeb3b"; }
                            else if (sub.overlayKey === "warnings") { subIcon = <WarningIcon />; accentColor = "#ff5252"; }
                            else if (sub.overlayKey === "alerts") { subIcon = <AlertIcon />; accentColor = "#ffa726"; }
                            else if (sub.overlayKey === "tropical") { subIcon = <HurricaneIcon />; accentColor = "#4dd0e1"; }
                            else if (sub.isSpc) {
                              subIcon = <OutlookIcon />;
                              accentColor = ({ day1: "#ffa366", day2: "#e8a050", day3: "#d09040" } as any)[sub.spcKey!];
                            }
                            else if (sub.isLayerHide) { subIcon = <EyeOffIcon />; accentColor = t.textSecondary; }
                            else if (sub.isToggle) { subIcon = <EyeOffIcon />; accentColor = t.textSecondary; }
                            const isOT = sub.isOverlay || sub.isToggle || sub.isLayerHide || sub.isSpc;
                            return (
                              <button key={sub.id}
                                disabled={isDisabled}
                                onClick={() => {
                                  if (isLocked) { requestUpsell(sub.label); return; }
                                  if (sub.isOverlay) toggleOverlay(sub.overlayKey!);
                                  // SPC picks are exclusive: tapping the lit one
                                  // turns the outlook off; tapping another swaps
                                  // the product in place (single parent value).
                                  else if (sub.isSpc) { props.setSpcOutlook(props.spcOutlook === sub.spcKey ? null : sub.spcKey!); }
                                  else if (sub.isLayerHide) { if (!isDisabled) props.setHidePrecip(!props.hidePrecip); }
                                  else if (sub.isToggle) toggleToggle(sub.toggleKey!);
                                  else props.setActiveLayer(props.activeLayer === sub.layerKey ? null : sub.layerKey!);
                                }}
                                onMouseEnter={() => setHoveredItem(sub.id)} onMouseLeave={() => setHoveredItem(null)}
                                style={{ width: "100%", display: "flex", alignItems: "center", gap: 9, padding: "7px 16px 7px 50px", background: hoveredItem === sub.id && !isDisabled ? t.subHoverBg : "transparent", border: "none", cursor: isDisabled ? "default" : "pointer", opacity: isDisabled ? 0.4 : isLocked ? 0.55 : 1 }}>
                                {isOT ? <div style={{ width: 14, height: 14, display: "flex", alignItems: "center", justifyContent: "center", color: isSubActive ? accentColor : t.textDimmer }}>{subIcon}</div>
                                  : <div style={{ width: 5, height: 5, borderRadius: "50%", background: isSubActive ? t.accent : "transparent", border: `1.5px solid ${isSubActive ? t.accent : t.textDimmer}`, flexShrink: 0 }} />}
                                <span style={{ fontSize: 12.5, color: isSubActive ? (isOT ? accentColor : t.textPrimary) : t.textMuted, fontWeight: isSubActive ? 500 : 400 }}>{sub.label}</span>
                                {isLocked && <div style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}><LockIcon /></div>}
                                {!isLocked && (sub.isOverlay || sub.isSpc) && isSubActive && <div style={{ marginLeft: "auto", width: 5, height: 5, borderRadius: "50%", background: accentColor, boxShadow: `0 0 6px ${accentColor}80` }} />}
                              </button>
                            );
                          })}
                        </div>
                      </AnimatedDropdown>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Collapsed icons — normal screens only */}
          {!isSmall && !effectiveExpanded && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "8px 0 10px", gap: 2 }}>
              {MENU.map((section) => {
                const isActive = isAnySectionItemActive(section) || isSectionLayerActive(section);
                const Icon = section.icon;
                return (
                  <div key={section.id} style={{ position: "relative" }}>
                    <button onClick={() => handleCollapsedIconClick(section)}
                      onMouseEnter={() => setTooltipId(section.id)} onMouseLeave={() => setTooltipId(null)}
                      style={{ width: 38, height: 38, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: isActive ? t.accentMed : "transparent", border: "none", cursor: "pointer", color: isActive ? t.accent : t.iconInactive, position: "relative", opacity: isSectionLocked(section.id) ? 0.55 : 1 }}>
                      <Icon />
                      {isSectionLocked(section.id) ? (
                        <div style={{ position: "absolute", top: 2, right: 2 }}><LockIcon size={9} /></div>
                      ) : hasLockedSubItem(section) ? (
                        <div style={{ position: "absolute", top: 2, right: 2, opacity: 0.6 }}><LockIcon size={9} /></div>
                      ) : null}
                    </button>
                    <Tooltip text={section.label} visible={tooltipId === section.id} />
                  </div>
                );
              })}
            </div>
          )}
            </>
          )}
        </div>
      </div>

      {/* ─── Small-screen long-press layer flyout ──────────────────────────
          Opens to the LEFT of the icon rail (the rail hugs the right edge, so
          there is no room on the other side) and is `position: fixed` so it
          escapes the menu pill's `overflow: hidden`.

          Locked rows are SHOWN here, dimmed with a padlock, matching both the
          desktop dropdown and the windy.com convention: a premium feature the
          user can see and tap (into an upsell) is discoverable, whereas one
          that is silently absent just makes the free build look like it has
          fewer features than it does. Tapping a padlocked row changes nothing
          and leaves the panel open so the locks stay in view. */}
      {isSmall && flyout && (() => {
        const section = MENU.find((s) => s.id === flyout.sectionId);
        if (!section) return null;
        const subs = flyoutSubItems(section);
        if (subs.length < 2) return null;
        // Keep the panel on screen: it's anchored to the icon's top edge but
        // clamped so a rail icon low in a short viewport can't push it off.
        const EST_ROW = 34, PAD = 12;
        const estH = subs.length * EST_ROW + 34;
        const top = Math.max(8, Math.min(flyout.top - 6, window.innerHeight - estH - PAD));
        return (
          <>
            {/* Tap-anywhere-else backdrop. */}
            <div onClick={() => setFlyout(null)}
              style={{ position: "fixed", inset: 0, zIndex: 70, background: "transparent" }} />
            <div style={{
              position: "fixed", top, right: 62, zIndex: 71,
              minWidth: 158, maxWidth: "calc(100vw - 80px)",
              background: t.panelBg, backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)" as any,
              border: `1px solid ${t.panelBorder}`, borderRadius: 10,
              boxShadow: t.panelShadow, overflow: "hidden",
              animation: "hintFadeIn 0.16s ease-out",
            }}>
              <div style={{
                padding: "7px 12px 6px", borderBottom: `1px solid ${t.divider}`,
                fontSize: 10.5, letterSpacing: 0.4, textTransform: "uppercase",
                color: t.textMuted, fontWeight: 600, whiteSpace: "nowrap",
              }}>{section.label}</div>
              <div style={{ padding: "3px 0 4px" }}>
                {subs.map((sub) => {
                  const hideTargetActive = sub.isLayerHide ? props.activeLayer === sub.hideLayerKey : false;
                  const isSubActive = sub.isOverlay ? (overlays as any)[sub.overlayKey!]
                    : sub.isSpc ? props.spcOutlook === sub.spcKey
                    : sub.isLayerHide ? props.hidePrecip
                    : sub.isToggle ? (toggles as any)[sub.toggleKey!]
                    : props.activeLayer === sub.layerKey;
                  // Situational disabling still applies: the radar-companion
                  // overlays are meaningless off radar, and "Hide Precip" only
                  // when radar is the active layer. Unlike a tier lock this is
                  // temporary, so the row stays visible and just dims.
                  const isDisabled = sub.isLayerHide ? !hideTargetActive
                    : sub.isOverlay ? (RADAR_ONLY_OVERLAYS.has(sub.overlayKey!) && props.activeLayer !== "radar")
                    : false;
                  let subIcon: JSX.Element | null = null, accentColor = t.accent;
                  if (sub.overlayKey === "lightning") { subIcon = <LightningIcon />; accentColor = "#ffeb3b"; }
                  else if (sub.overlayKey === "warnings") { subIcon = <WarningIcon />; accentColor = "#ff5252"; }
                  else if (sub.overlayKey === "alerts") { subIcon = <AlertIcon />; accentColor = "#ffa726"; }
                  else if (sub.overlayKey === "tropical") { subIcon = <HurricaneIcon />; accentColor = "#4dd0e1"; }
                  else if (sub.isSpc) {
                    subIcon = <OutlookIcon />;
                    accentColor = ({ day1: "#ffa366", day2: "#e8a050", day3: "#d09040" } as any)[sub.spcKey!];
                  }
                  else if (sub.isLayerHide || sub.isToggle) { subIcon = <EyeOffIcon />; accentColor = t.textSecondary; }
                  const isOT = sub.isOverlay || sub.isToggle || sub.isLayerHide || sub.isSpc;
                  return (
                    <button key={sub.id}
                      disabled={isDisabled}
                      onClick={() => {
                        if (sub.isOverlay) toggleOverlay(sub.overlayKey!);
                        else if (sub.isSpc) props.setSpcOutlook(props.spcOutlook === sub.spcKey ? null : sub.spcKey!);
                        else if (sub.isLayerHide) { if (!isDisabled) props.setHidePrecip(!props.hidePrecip); }
                        else if (sub.isToggle) toggleToggle(sub.toggleKey!);
                        else props.setActiveLayer(props.activeLayer === sub.layerKey ? null : sub.layerKey!);
                        // Overlay/toggle rows are things you often flip in a
                        // batch (warnings + alerts together), so keep the panel
                        // open for them. Picking a base layer or an SPC day is a
                        // terminal, exclusive choice, so close.
                        if (!sub.isOverlay && !sub.isToggle && !sub.isLayerHide) setFlyout(null);
                      }}
                      style={{
                        width: "100%", display: "flex", alignItems: "center", gap: 9,
                        padding: "8px 12px", background: "transparent", border: "none",
                        cursor: isDisabled ? "default" : "pointer", opacity: isDisabled ? 0.4 : 1,
                        textAlign: "left",
                      }}>
                      {isOT
                        ? <div style={{ width: 14, height: 14, display: "flex", alignItems: "center", justifyContent: "center", color: isSubActive ? accentColor : t.textDimmer, flexShrink: 0 }}>{subIcon}</div>
                        : <div style={{ width: 5, height: 5, borderRadius: "50%", background: isSubActive ? t.accent : "transparent", border: `1.5px solid ${isSubActive ? t.accent : t.textDimmer}`, flexShrink: 0 }} />}
                      <span style={{ fontSize: 12.5, whiteSpace: "nowrap", color: isSubActive ? (isOT ? accentColor : t.textPrimary) : t.textMuted, fontWeight: isSubActive ? 500 : 400 }}>{sub.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        );
      })()}

      {/* ─── Settings panel (single, fixed, escapes the clipped menu pill) ─── */}
      {showSettings && (
        <>
          {/* Click-outside backdrop — taps anywhere else close the panel. */}
          <div onClick={() => setShowSettings(false)}
            style={{ position: "fixed", inset: 0, zIndex: 69, background: "transparent" }} />
          <div style={{
            position: "fixed",
            top: isSmall ? 56 : 60,
            right: 14,
            width: isSmall ? "calc(100vw - 28px)" : 244,
            maxWidth: 320,
            maxHeight: isSmall ? "calc(100vh - 72px)" : "calc(100vh - 76px)",
            overflowY: "auto",
            background: t.panelBg, backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)" as any,
            border: `1px solid ${t.panelBorder}`, borderRadius: 12,
            boxShadow: t.panelShadow, padding: "14px 16px", zIndex: 70,
            display: "flex", flexDirection: "column", gap: 14,
          }}>
            {settingsPanelBody}
          </div>
        </>
      )}

      {/* ─── Map controls (top-left) ───────────────────────────────── */}
      {/* Hidden on very short viewports (<=350px tall) — see isVeryShort. */}
      {!isVeryShort && (
      <div style={{ position: "absolute", top: 14, left: 14, zIndex: 40, display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ ...mapCtrlPill, display: "flex", flexDirection: "column" }}>
          {mapCtrlBtn(() => props.mapRef.current?.zoomIn(),
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          )}
          <div style={{ height: 1, background: t.divider, margin: "0 6px" }} />
          {mapCtrlBtn(() => props.mapRef.current?.zoomOut(),
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12" /></svg>
          )}
        </div>
        <div style={mapCtrlPill}>
          {mapCtrlBtn(() => { props.mapRef.current?.easeTo({ bearing: 0, pitch: 0, duration: 800 }); },
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" stroke="currentColor" opacity="0.6" />
              <polygon points="12,4 14.5,13 12,11.5 9.5,13" fill="currentColor" stroke="none" opacity="1" />
              <polygon points="12,20 9.5,13 12,14.5 14.5,13" fill="currentColor" stroke="none" opacity="0.45" />
              <text x="12" y="3.5" textAnchor="middle" fontSize="4.5" fill="currentColor" stroke="none" fontWeight="700" fontFamily="Roboto, sans-serif" opacity="0.9">N</text>
            </svg>
          )}
        </div>
        <div style={mapCtrlPill}>
          {mapCtrlBtn(() => {
            if (!navigator.geolocation) return;
            navigator.geolocation.getCurrentPosition(
              (pos) => { props.mapRef.current?.flyTo({ center: [pos.coords.longitude, pos.coords.latitude], zoom: 8, duration: 1500 }); },
              () => {},
              { enableHighAccuracy: true, timeout: 10000 }
            );
          },
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="4" /><line x1="12" y1="2" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="22" /><line x1="2" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="22" y2="12" />
            </svg>
          )}
        </div>
      </div>
      )}

      {/* ─── Timeline + Legend (bottom) ─────────────────────────────── */}
      {/* Stay mounted for the whole time a layer is active. We intentionally
          do NOT gate on scrubberLength here: when the user switches layers,
          the new layer's keyframes haven't arrived yet so scrubberLength is
          briefly 0. Gating on it would unmount the entire panel and remount
          it once frames load — that's the "timeline fully reloads" flash.
          Instead the panel stays put and only its inner contents (scrubber
          position + legend) refresh as the new layer's data comes in.
          Also mounted whenever the Storm Outlook (SPC) is on — even with no
          base layer — because the outlook's severity legend lives here (the
          overlay has no popups; the legend is the only color key). */}
      {(props.activeLayer || props.spcOutlook) && !isVeryShort && (
        <div data-timeline-pill style={{
          position: "absolute", bottom: M.bottom, left: "50%", transform: "translateX(-50%)",
          zIndex: 40,
          // On lg/md the pill is content-sized (capped so it can never run off
          // the edge); at sm/xs it takes the viewport width minus a margin so
          // the scrubber and legend get every pixel available.
          width: M.outerWidth, maxWidth: M.outerMaxWidth,
        }}>
          <div style={{
            background: t.timelineBg, backdropFilter: "blur(16px)",
            border: `1px solid ${t.timelineBorder}`, borderRadius: M.radius,
            padding: 0, display: "flex", flexDirection: "column",
            // minWidth is only a floor on the roomy tiers; below `md` it's 0 so
            // the pill can shrink with the viewport instead of overflowing it.
            minWidth: M.minWidth, maxWidth: "100%", overflow: "hidden",
          }}>
            {/* Scrubber — only when a base layer is active. When the Storm
                Outlook is on by itself there's nothing to scrub, so the pill
                shows just the severity legend below. */}
            {props.activeLayer && (
            <div style={{ padding: M.rowPad, display: "flex", alignItems: "center", gap: M.rowGap, position: "relative" }}>
              <button onClick={() => props.setPlaying(!props.playing)} style={{
                width: M.playSize, height: M.playSize, borderRadius: M.playRadius,
                background: props.playing ? t.accentBg : t.playBg,
                border: `1px solid ${props.playing ? t.accentBorder : t.playBorder}`,
                cursor: "pointer", color: props.playing ? t.accent : t.textSecondary,
                fontSize: M.playFont, display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                {props.playing ? "❚❚" : "▶"}
              </button>

              <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0 }}>
                {/* Past / Future labels — RADAR ONLY.
                    Radar is the sole layer with a past↔future split worth
                    labelling: on free its future (nowcast) is premium, so it
                    shows the PAST label + a tappable "FUTURE 🔒" upsell chip; on
                    premium it shows plain PAST / FUTURE split at the boundary.
                    Every other layer now carries a full, ungated timeline but
                    renders a PLAIN scrubber with no labels — the past/future
                    distinction isn't meaningful enough there to clutter the UI. */}
                {M.showPastFuture && props.activeLayer === "radar" && hasFuture && props.keyframes.length > 0 && (IS_FREE ? (
                  <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
                    <span style={{ flex: 1, fontSize: 7, color: t.textDim, fontFamily: "Roboto, sans-serif", letterSpacing: 0.8, textAlign: "center" }}>PAST</span>
                    <button onClick={() => requestUpsell("Future radar (nowcast)")}
                      title="Future radar — Premium"
                      style={{ display: "flex", alignItems: "center", gap: 3, background: "none", border: "none", cursor: "pointer", padding: "0 2px", flexShrink: 0 }}>
                      <span style={{ fontSize: 7, color: "#f5a623", fontFamily: "Roboto, sans-serif", letterSpacing: 0.8 }}>FUTURE</span>
                      <LockIcon size={7} />
                    </button>
                  </div>
                ) : (() => {
                  const firstForecastIdx = props.keyframes.findIndex((kf: any) => kf.isForecast);
                  if (firstForecastIdx < 0) return null;
                  const pastPct = (firstForecastIdx / props.scrubberLength) * 100;
                  return (
                    <div style={{ display: "flex", marginBottom: 4 }}>
                      <span style={{ fontSize: 7, color: t.textDim, fontFamily: "Roboto, sans-serif", letterSpacing: 0.8, width: `${pastPct}%`, textAlign: "center" }}>PAST</span>
                      <span style={{ fontSize: 7, color: t.textDim, fontFamily: "Roboto, sans-serif", letterSpacing: 0.8, width: `${100 - pastPct}%`, textAlign: "center" }}>FUTURE</span>
                    </div>
                  );
                })())}

                {/* Custom scrubber track.
                    The grey rail is a single element pinned left:0/right:0, so it
                    is ALWAYS the full width of the column and never grows or
                    shrinks with frame count, playback position, or load state —
                    this fixes the "grey bar grows" regression that came from the
                    native range input's intrinsic fill rendering.
                    Layered children (all absolutely positioned within the rail):
                      • accent fill  — from left to the current thumb position
                      • boundary tick — a thin vertical mark at the past/future split
                      • thumb        — a draggable knob at the current position
                    A transparent native <input type="range"> sits on top to keep
                    full keyboard + drag interaction; its own track/thumb are hidden
                    via the scrubber-input CSS class (injected once below). */}
                {(() => {
                  const max = framesReady ? props.scrubberLength - 1 : 1;
                  const val = framesReady ? props.currentIdx : 0;
                  const pct = max > 0 ? Math.max(0, Math.min(100, (val / max) * 100)) : 0;
                  // Past/future boundary + nowcast fill color are RADAR ONLY, to
                  // match the labels above. Other layers now carry forecast
                  // frames too, but we deliberately render them as one
                  // undivided, single-color timeline — no tick, no color shift
                  // at "now" — so the scrubber stays clean where the past/future
                  // split isn't a labelled concept.
                  const isRadar = props.activeLayer === "radar";
                  const fi = props.keyframes.findIndex((kf: any) => kf.isForecast);
                  const boundaryPct = (isRadar && hasFuture && fi > 0 && props.scrubberLength > 1)
                    ? (fi / (props.scrubberLength - 1)) * 100
                    : null;
                  const fillColor = (isRadar && isForecast) ? t.nowcast : t.accent;
                  return (
                    <div style={{
                      position: "relative", width: "100%",
                      height: M.scrubberH, display: "flex", alignItems: "center",
                      opacity: framesReady ? 1 : 0.4,
                    }}>
                      {/* Fixed-width grey rail */}
                      <div style={{
                        position: "absolute", left: 0, right: 0, height: 4,
                        borderRadius: 2, background: t.playBg, pointerEvents: "none",
                      }}>
                        {/* Accent fill up to the thumb */}
                        <div style={{
                          position: "absolute", left: 0, top: 0, bottom: 0,
                          width: `${pct}%`, background: fillColor, borderRadius: 2,
                          transition: props.playing ? "none" : "width 0.1s linear",
                        }} />
                      </div>
                      {/* Past/future divider tick */}
                      {boundaryPct != null && (
                        <div style={{
                          position: "absolute", left: `${boundaryPct}%`,
                          top: "50%", transform: "translate(-50%, -50%)",
                          width: 2, height: M.tickH,
                          background: t.textDim, borderRadius: 1, pointerEvents: "none",
                        }} />
                      )}
                      {/* Thumb */}
                      {framesReady && (
                        <div style={{
                          position: "absolute", left: `${pct}%`,
                          top: "50%", transform: "translate(-50%, -50%)",
                          width: M.thumb, height: M.thumb,
                          borderRadius: "50%", background: fillColor,
                          border: `2px solid ${t.timelineBg}`,
                          boxShadow: `0 0 0 1px ${fillColor}80`,
                          pointerEvents: "none",
                        }} />
                      )}
                      {/* Transparent native input on top for interaction */}
                      <input className="scrubber-input" type="range" min={0} max={max}
                        value={val} step={0.01} disabled={!framesReady}
                        onChange={(e: any) => props.onScrub(parseFloat(e.target.value))}
                        style={{
                          position: "absolute", left: 0, right: 0, width: "100%",
                          margin: 0, background: "transparent",
                          cursor: framesReady ? "pointer" : "default",
                        }} />
                    </div>
                  );
                })()}
              </div>

              {/* Time readout. Below `md` the MM/DD suffix is dropped — the
                  date is the least load-bearing part of the string and buys
                  ~60px back for the scrubber on a narrow viewport. */}
              <span style={{
                fontFamily: "Roboto, sans-serif", fontSize: M.timeFont,
                color: framesReady ? ((props.activeLayer === "radar" && isForecast) ? t.nowcast : t.accent) : t.textDim,
                whiteSpace: "nowrap", minWidth: M.timeMinW, textAlign: "right", flexShrink: 0,
              }}>
                {framesReady ? (M.shortTime ? timeLabelShort : timeLabel) : "Loading…"}
              </span>
            </div>
            )}

            {/* Preload progress */}
            {props.activeLayer && !fullyLoaded && (
              <div style={{ padding: M.progressPad, display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ flex: 1, height: 2, background: t.playBg, borderRadius: 1, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${progressPct}%`, background: t.textDim, transition: "width 0.2s ease" }} />
                </div>
                <span style={{ fontSize: 9, color: t.textDim, fontFamily: "Roboto, sans-serif", minWidth: 30, textAlign: "right" }}>
                  {progressPct}%
                </span>
                {failedFrames > 0 && (
                  <span style={{ fontSize: 9, color: "#f5a623", fontFamily: "Roboto, sans-serif", whiteSpace: "nowrap" }}>
                    {failedFrames} unavailable
                  </span>
                )}
              </div>
            )}

            {/* Legend.
                Each column is a flex item that may SHRINK (`flex: 1 1 <basis>`
                with minWidth 0) rather than a rigid 100px block. On lg/md the
                basis sets the natural size exactly as before; at sm/xs the
                columns compress to fit whatever the viewport allows, so the
                RAIN / SLEET / SNOW bars all stay inside the pill instead of the
                third one being clipped off the right edge.
                The title ellipsizes as a last resort, and the tick labels under
                each gradient are thinned (7 → 5 → 3 → endpoints) so they never
                collide as the bar narrows. */}
            {M.showLegend && props.activeLayer && legendItems.length > 0 && (
              <>
                <div style={{ height: 1, background: t.divider, margin: "0 14px" }} />
                <div style={{
                  padding: M.legendPad, display: "flex", gap: M.legendGap,
                  alignItems: "flex-start", justifyContent: "center",
                  flexWrap: "nowrap", width: "100%",
                }}>
                  {legendItems.map((p: any) => {
                    const title = (M.shortLegendLabel && LEGEND_SHORT_LABELS[p.label]) || p.label;
                    const ticks = thinTicks(p.labels ?? [p.low, p.high], M.maxTicks);
                    return (
                      <div key={p.label} style={{
                        display: "flex", flexDirection: "column", alignItems: "center",
                        flex: `1 1 ${M.legendMinW}px`, minWidth: 0,
                        // A lone legend (temperature, wind…) is allowed to run
                        // the full width; multi-column sets stay tidy.
                        maxWidth: legendItems.length === 1 ? "none" : 200,
                      }}>
                        <span style={{
                          fontSize: M.legendLabelFont, fontWeight: 700,
                          color: p.colorKey === "accent" ? t.accent : p.color,
                          fontFamily: "Roboto, sans-serif", letterSpacing: M.legendLabelSpacing,
                          marginBottom: 3, maxWidth: "100%",
                          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        }} title={p.label}>{title}</span>
                        <div style={{ width: "100%", height: M.legendBarH, borderRadius: M.legendBarH / 2, background: p.gradient }} />
                        <div style={{ display: "flex", justifyContent: "space-between", width: "100%", marginTop: 2 }}>
                          {ticks.map((lbl: string, i: number) => (
                            <span key={i} style={{
                              fontSize: M.legendTickFont, color: t.textDim,
                              fontFamily: "Roboto, sans-serif", whiteSpace: "nowrap",
                            }}>{lbl}</span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* SPC Storm Outlook severity legend — shown whenever the outlook
                is on, on EVERY screen size (the outlook has no click popups,
                so this legend is the only key to the risk-tier colors).
                Discrete swatches lowest → highest: TSTM through HIGH. */}
            {props.spcOutlook && (
              <>
                {(props.activeLayer || (M.showLegend && legendItems.length > 0)) && (
                  <div style={{ height: 1, background: t.divider, margin: "0 14px" }} />
                )}
                <div style={{ padding: M.spcPad }}>
                  <div style={{
                    fontSize: M.spcTitleFont, fontWeight: 700, color: t.textDim,
                    fontFamily: "Roboto, sans-serif", letterSpacing: 1.5,
                    textAlign: "center", marginBottom: 4, textTransform: "uppercase",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>
                    {SPC_PRODUCTS[props.spcOutlook].label} (US)
                  </div>
                  {/* Risk tiers wrap onto a second line rather than clipping —
                      six swatches will not fit one row on a phone. */}
                  <div style={{ display: "flex", gap: M.spcGap, justifyContent: "center", flexWrap: "wrap", rowGap: 4 }}>
                    {SPC_LEGEND_ITEMS.map((item) => (
                      <div key={item.dn} title={item.label}
                        style={{ display: "flex", alignItems: "center", gap: M.spcSwatch > 9 ? 5 : 3 }}>
                        <span style={{
                          width: M.spcSwatch, height: M.spcSwatch,
                          borderRadius: 3, background: item.color, flexShrink: 0,
                          border: "1px solid rgba(0,0,0,0.25)",
                        }} />
                        <span style={{
                          fontSize: M.spcFont, color: t.textDim,
                          fontFamily: "Roboto, sans-serif", fontWeight: 600,
                          letterSpacing: 0.4, whiteSpace: "nowrap",
                        }}>
                          {M.spcShortLabels ? item.short : item.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes hintFadeIn { from { opacity: 0; transform: translateX(4px); } to { opacity: 1; transform: translateX(0); } }
        /* Invisible scrubber input: the visible track/fill/thumb are custom
           divs underneath; this input only captures pointer/keyboard input.
           opacity:0 (rather than background:transparent on each pseudo-element)
           is deliberate — iOS Safari repaints its own default thumb, a white
           rounded rect with a drop shadow, whenever the thumb has no background
           of its own, ignoring -webkit-appearance:none. Zeroing the whole
           element suppresses every native part on every engine. The thumb stays
           sized so the full bar remains draggable. */
        input.scrubber-input {
          -webkit-appearance: none; appearance: none;
          background: transparent; height: 18px; outline: none; opacity: 0;
          -webkit-tap-highlight-color: transparent;
          -webkit-user-select: none; user-select: none;
        }
        input.scrubber-input::-webkit-slider-runnable-track { background: transparent; height: 18px; border: none; }
        input.scrubber-input::-moz-range-track { background: transparent; height: 18px; border: none; }
        input.scrubber-input::-moz-range-progress { background: transparent; }
        input.scrubber-input::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 18px; height: 18px; background: transparent; border: none; box-shadow: none; cursor: pointer; }
        input.scrubber-input::-moz-range-thumb { width: 18px; height: 18px; background: transparent; border: none; cursor: pointer; }
        input.scrubber-input:disabled { cursor: default; }
      `}</style>
    </ThemeContext.Provider>
  );
}